import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMe, changePassword, MeResponse } from '../../api/auth';
import { getMyZones } from '../../api/zones';
import { createPoint, getUserPoints } from '../../api/points';
import { Zone, isZoneActive } from '../../types/zone';
import { toastError, toastSuccess } from '../../utils/toast';

const isSyntheticEmail = (email: string): boolean => {
  return email.startsWith('oauth_') && email.endsWith('@wearepixels.local');
};

export const ProfileSection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [me, setMe]               = useState<MeResponse | null>(null);
  const [loadError, setLoadError] = useState('');
  const [zones, setZones]         = useState<Zone[]>([]);
  const [pointsCount, setPointsCount] = useState(0);
  const [zonePointName, setZonePointName] = useState('');
  const [selectedZoneId, setSelectedZoneId] = useState('');
  const [zonePointLoading, setZonePointLoading] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd]         = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdError, setPwdError]     = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  useEffect(() => {
    getMe()
      .then(res => setMe(res.data))
      .catch(() => setLoadError(t('profile.loadError')));
    getMyZones().then(res => setZones(res.data)).catch(() => {});
    getUserPoints().then(res => setPointsCount(res.data.length)).catch(() => {});
  }, []); // eslint-disable-line

  const activeZones = zones.filter(isZoneActive);

  useEffect(() => {
    if (activeZones.length === 0) {
      setSelectedZoneId('');
      return;
    }
    if (!activeZones.some(zone => String(zone.id) === selectedZoneId)) {
      setSelectedZoneId(String(activeZones[0].id));
    }
  }, [activeZones, selectedZoneId]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError(''); setPwdSuccess('');
    if (newPwd !== confirmPwd) { setPwdError(t('profile.passwordsNoMatch')); return; }
    if (newPwd.length < 6)     { setPwdError(t('profile.passwordTooShort'));  return; }
    setPwdLoading(true);
    try {
      await changePassword(currentPwd, newPwd);
      setPwdSuccess(t('profile.passwordChanged'));
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err: any) {
      setPwdError(err.response?.data?.error || t('profile.passwordChangeError'));
    } finally {
      setPwdLoading(false);
    }
  };

  const handleCreatePointInZone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedZoneId) {
      toastError(t('profile.zonePointZoneRequired'));
      return;
    }

    setZonePointLoading(true);
    try {
      const res = await createPoint(zonePointName.trim(), undefined, undefined, Number(selectedZoneId));
      toastSuccess(t('profile.zonePointCreated'));
      navigate(`/game/${res.data.id}`);
    } catch (err: any) {
      toastError(err?.response?.data?.error || t('profile.zonePointError'));
    } finally {
      setZonePointLoading(false);
    }
  };

  return (
    <div className="profile-section">
      {loadError && (
        <div className="auth-form__msg auth-form__msg--error">{loadError}</div>
      )}

      {me && (
        <div className="profile-section__info">
          <div className="profile-section__row">
            <span className="profile-section__label">{t('profile.emailLabel')}</span>
            <span className="profile-section__value">
              {isSyntheticEmail(me.email) ? (
                <div>
                  <div>{me.email}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--px-text-dim)', marginTop: '0.25rem' }}>
                    {t('profile.syntheticEmailHint')}
                  </div>
                </div>
              ) : (
                me.email
              )}
            </span>
          </div>
          <div className="profile-section__row">
            <span className="profile-section__label">{t('profile.accountType')}</span>
            <span className="profile-section__value profile-section__value--dim">
              {me.oauth_provider
                ? t('profile.oauthAccount', { provider: me.oauth_provider })
                : t('profile.passwordAccount')}
            </span>
          </div>
          <div className="profile-section__row">
            <span className="profile-section__label">{t('profile.registrationDate')}</span>
            <span className="profile-section__value profile-section__value--dim">
              {new Date(me.created_at).toLocaleDateString()}
            </span>
          </div>
          <div className="profile-section__row">
            <span className="profile-section__label">{t('profile.zonePointLimit')}</span>
            <span className="profile-section__value profile-section__value--dim">
              {pointsCount} / {me.max_points}
            </span>
          </div>
        </div>
      )}

      {me && (
        <div className="profile-section__password">
          <h3 className="profile-section__subheading">{t('profile.zonePointTitle')}</h3>

          <form onSubmit={handleCreatePointInZone} className="profile-section__form">
            <div className="auth-form__field">
              <label className="auth-form__label">{t('profile.zonePointZone')}</label>
              <select
                className="auth-form__input"
                value={selectedZoneId}
                onChange={e => setSelectedZoneId(e.target.value)}
                disabled={activeZones.length === 0 || zonePointLoading}
              >
                {activeZones.length === 0 ? (
                  <option value="">{t('profile.zonePointNoZones')}</option>
                ) : (
                  activeZones.map(zone => (
                    <option key={zone.id} value={zone.id}>
                      {(zone.name || `#${zone.id}`) + ` (${zone.x_min},${zone.y_min})-(${zone.x_max},${zone.y_max})`}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="auth-form__field">
              <label className="auth-form__label">{t('profile.zonePointName')}</label>
              <input
                className="auth-form__input"
                type="text"
                value={zonePointName}
                onChange={e => setZonePointName(e.target.value)}
                placeholder={t('createPoint.namePlaceholder')}
                maxLength={50}
              />
            </div>

            <div className="auth-form__msg" style={{ color: 'var(--px-text-dim)', background: 'transparent', borderColor: 'var(--px-border)' }}>
              {activeZones.length === 0
                ? t('profile.zonePointNoZonesHint')
                : pointsCount >= me.max_points
                  ? t('profile.zonePointLimitReached')
                  : t('profile.zonePointHint')}
            </div>

            <button
              className="auth-form__submit"
              type="submit"
              disabled={zonePointLoading || activeZones.length === 0 || pointsCount >= me.max_points}
            >
              {zonePointLoading ? t('profile.zonePointCreating') : t('profile.zonePointBtn')}
            </button>
          </form>
        </div>
      )}

      {me && me.has_password && (
        <div className="profile-section__password">
          <h3 className="profile-section__subheading">{t('profile.changePassword')}</h3>

          {pwdError   && <div className="auth-form__msg auth-form__msg--error">{pwdError}</div>}
          {pwdSuccess && <div className="auth-form__msg auth-form__msg--success">{pwdSuccess}</div>}

          <form onSubmit={handleChangePassword} className="profile-section__form">
            <div className="auth-form__field">
              <label className="auth-form__label">{t('profile.currentPassword')}</label>
              <input
                className="auth-form__input"
                type="password"
                value={currentPwd}
                onChange={e => setCurrentPwd(e.target.value)}
                placeholder="••••••"
                autoComplete="current-password"
              />
            </div>
            <div className="auth-form__field">
              <label className="auth-form__label">{t('profile.newPassword')}</label>
              <input
                className="auth-form__input"
                type="password"
                value={newPwd}
                onChange={e => setNewPwd(e.target.value)}
                placeholder="••••••"
                autoComplete="new-password"
              />
            </div>
            <div className="auth-form__field">
              <label className="auth-form__label">{t('profile.repeatPassword')}</label>
              <input
                className="auth-form__input"
                type="password"
                value={confirmPwd}
                onChange={e => setConfirmPwd(e.target.value)}
                placeholder="••••••"
                autoComplete="new-password"
              />
            </div>
            <button className="auth-form__submit" type="submit" disabled={pwdLoading}>
              {pwdLoading ? t('profile.saving') : t('profile.saveBtn')}
            </button>
          </form>
        </div>
      )}

      {me && !me.has_password && (
        <div className="auth-form__msg" style={{ color: 'var(--px-text-dim)', background: 'transparent', borderColor: 'var(--px-border)' }}>
          {t('profile.oauthPasswordNote')}
        </div>
      )}
    </div>
  );
};
